import type { LocationSuggestion } from '@ixigo/cab-core';
import { isTripType, TRIP_TYPE } from '@ixigo/cab-core/location';
import type { SearchFormData } from '@/components/search-form';

export interface ReviewSearchParams extends Required<SearchFormData> {
	cabId: string;
	/**
	 * Action types the user already accepted on the home sheet (e.g. a
	 * near-airport confirmation), carried in the URL so the review fetch sends
	 * them on the first call instead of re-triggering the popup.
	 */
	actions?: string[];
}

/**
 * Parses SearchFormData from URL search parameters for the review page
 * @param searchParams - Record of search parameters from URL
 * @returns SearchFormData if valid, null if invalid or missing
 */
export function parseReviewSearchParams(
	searchParams: Record<string, unknown>,
): ReviewSearchParams | null {
	try {
		const { pickup, drop, dateTime, cabId, tripType, actions } = searchParams;

		if (
			!pickup ||
			!drop ||
			!dateTime ||
			!cabId ||
			typeof pickup !== 'string' ||
			typeof drop !== 'string' ||
			typeof dateTime !== 'string' ||
			typeof cabId !== 'string' ||
			!isValidISODateString(dateTime)
		) {
			return null;
		}

		const cleanPickup =
			pickup.startsWith('"') && pickup.endsWith('"')
				? pickup.slice(1, -1)
				: pickup;

		const cleanDrop =
			drop.startsWith('"') && drop.endsWith('"') ? drop.slice(1, -1) : drop;

		let parsedPickup: LocationSuggestion;
		let parsedDrop: LocationSuggestion;

		try {
			parsedPickup = JSON.parse(cleanPickup);
			parsedDrop = JSON.parse(cleanDrop);
		} catch {
			return null;
		}

		const isValidPickup = isValidLocationSuggestion(parsedPickup);
		const isValidDrop = isValidLocationSuggestion(parsedDrop);
		if (!isValidPickup || !isValidDrop) {
			return null;
		}

		const parsedActions = parseActions(actions);

		return {
			pickup: parsedPickup,
			drop: parsedDrop,
			dateTime,
			cabId,
			// Validate the URL param: a known trip type is used as-is; anything
			// else (absent on legacy links, or a malformed value) → "To Airport".
			tripType: isTripType(tripType) ? tripType : TRIP_TYPE.AIRPORT_DROP_OFF,
			...(parsedActions.length ? { actions: parsedActions } : {}),
		};
	} catch {
		return null;
	}
}

/**
 * Parse the optional `actions` URL param (a JSON-encoded string array) into a
 * clean string[]. Anything malformed or non-string is dropped — the fetch just
 * proceeds without accepted actions.
 */
function parseActions(value: unknown): string[] {
	if (typeof value !== 'string') return [];
	try {
		const parsed = JSON.parse(value);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter((item): item is string => typeof item === 'string');
	} catch {
		return [];
	}
}

/**
 * Validates if a string is a valid ISO date string
 * @param dateString - String to validate
 * @returns true if valid ISO date string, false otherwise
 */
function isValidISODateString(dateString: string): boolean {
	if (!dateString) return false;

	const date = new Date(dateString);
	return !Number.isNaN(date.getTime()) && date.toISOString() === dateString;
}

/**
 * Validates if an object is a valid LocationSuggestion
 * @param obj - Object to validate
 * @returns true if valid LocationSuggestion, false otherwise
 */
function isValidLocationSuggestion(obj: any): obj is LocationSuggestion {
	return (
		obj &&
		typeof obj === 'object' &&
		obj !== null &&
		(typeof obj.placeId === 'string' ||
			(typeof obj.lat === 'number' && typeof obj.lng === 'number'))
	);
}
